import React, { useState, useEffect } from 'react';
import {
  getPersonDetails,
  formatPersonName,
  getStatusConfig,
  getRiskLevelConfig,
} from '../../../services/networkIntelligence.service';

/**
 * Side panel component for viewing detailed person information
 * when investigating network connections.
 */
const PersonDetailsSidePanel = ({ personId, onClose, onNavigate, connectionInfo }) => {
  const [person, setPerson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (personId) {
      loadPersonDetails();
    }
  }, [personId]);

  const loadPersonDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPersonDetails(personId);
      if (data) {
        setPerson(data);
      } else {
        setError('Person not found');
      }
    } catch (err) {
      setError('Failed to load person details');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const statusConfig = getStatusConfig(person?.status || person?.Status);
  const complianceConfig = getStatusConfig(person?.complianceStatus || person?.ComplianceStatus);

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Person Details</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-blue-500 rounded-full transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {!isLoading && person && (
          <div className="mt-2">
            <div className="text-xl font-bold">{formatPersonName(person)}</div>
            <div className="text-blue-100 text-sm">ID: {personId}</div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {!isLoading && person && (
          <div className="space-y-4">
            {/* Status Badges */}
            <div className="flex flex-wrap gap-2">
              <span
                className="px-2 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
              >
                {statusConfig.label}
              </span>
              {person?.complianceStatus && (
                <span
                  className="px-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: complianceConfig.bgColor, color: complianceConfig.color }}
                >
                  {complianceConfig.label}
                </span>
              )}
            </div>

            {/* Connection Info (if viewing from network) */}
            {connectionInfo && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <h4 className="font-medium text-purple-800 text-sm mb-2">Connection Info</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Depth:</span>
                    <span className="ml-2 font-medium">
                      {connectionInfo.depth === 1 ? 'Direct' : `${connectionInfo.depth} hops`}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Interactions:</span>
                    <span className="ml-2 font-medium">{connectionInfo.interactionCount || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Strength:</span>
                    <span className="ml-2 font-medium">
                      {((connectionInfo.connectionWeight || 0) * 100).toFixed(0)}%
                    </span>
                  </div>
                  {connectionInfo.communityId !== null && connectionInfo.communityId !== undefined && (
                    <div>
                      <span className="text-gray-500">Community:</span>
                      <span className="ml-2 font-medium">#{connectionInfo.communityId}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Personal Info */}
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="font-medium text-gray-700 text-sm mb-2">Personal Information</h4>
              <div className="space-y-2 text-sm">
                {person.email && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-600">{person.email}</span>
                  </div>
                )}
                {person.phoneNumber && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-gray-600">{person.phoneNumber}</span>
                  </div>
                )}
                {person.gender && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-gray-600">{person.gender}</span>
                  </div>
                )}
                {person.dob && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-600">{person.dob}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Address */}
            {person.address && (
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-gray-700 text-sm mb-2">Address</h4>
                <div className="text-sm text-gray-600">
                  {person.address.street1 && <div>{person.address.street1}</div>}
                  {person.address.street2 && <div>{person.address.street2}</div>}
                  <div>
                    {person.address.city && `${person.address.city}, `}
                    {person.address.state && `${person.address.state} `}
                    {person.address.zipCode}
                  </div>
                </div>
              </div>
            )}

            {/* Visa/Immigration Info */}
            {person.peopleVisaMetadata && (
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-gray-700 text-sm mb-2">Immigration Info</h4>
                <div className="space-y-1 text-sm">
                  {person.peopleVisaMetadata.aNumber && (
                    <div>
                      <span className="text-gray-500">A-Number:</span>
                      <span className="ml-2 text-gray-700">{person.peopleVisaMetadata.aNumber}</span>
                    </div>
                  )}
                  {person.peopleVisaMetadata.countryOfOrigin && (
                    <div>
                      <span className="text-gray-500">Country:</span>
                      <span className="ml-2 text-gray-700">{person.peopleVisaMetadata.countryOfOrigin}</span>
                    </div>
                  )}
                  {person.peopleVisaMetadata.visaType && (
                    <div>
                      <span className="text-gray-500">Visa Type:</span>
                      <span className="ml-2 text-gray-700">{person.peopleVisaMetadata.visaType}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Organization */}
            {person.organization && (
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-medium text-gray-700 text-sm mb-2">Organization</h4>
                <div className="text-sm text-gray-600">
                  {person.organization.name || person.organization.Name}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="border-t p-4 space-y-2">
        <button
          onClick={() => onNavigate && onNavigate(personId)}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Open Full Profile
        </button>
        <button
          onClick={() => {
            // Open in Pattern of Life with this person
            window.open(`/pattern-of-life?personId=${personId}`, '_blank');
          }}
          className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          View Pattern of Life
        </button>
      </div>
    </div>
  );
};

export default PersonDetailsSidePanel;
